import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search,
  Plus,
  Minus,
  Receipt,
  RotateCcw,
  ArrowLeftRight,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { formatCurrency } from './posUtils';

const RefundDialog = ({
  open,
  onOpenChange,
  refundSearchTerm,
  setRefundSearchTerm,
  searchSaleForRefund,
  loadingRefund,
  refundOriginalSale,
  refundType,
  setRefundType,
  refundItems,
  updateRefundItemQuantity,
  refundReason,
  setRefundReason,
  processRefund,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw size={20} className="text-amber-600" />
            Process Return / Exchange
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Receipt Lookup */}
          <div className="space-y-2">
            <Label>Receipt Number</Label>
            <div className="flex gap-2">
              <Input
                value={refundSearchTerm}
                onChange={(e) => setRefundSearchTerm(e.target.value)}
                placeholder="Enter receipt number (e.g., RCP-20260315...)"
                onKeyDown={(e) => e.key === 'Enter' && searchSaleForRefund()}
                data-testid="refund-receipt-input"
              />
              <Button 
                onClick={searchSaleForRefund}
                disabled={loadingRefund}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loadingRefund ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search size={18} />}
              </Button>
            </div>
          </div>

          {/* Original Sale Info */}
          {refundOriginalSale && (
            <>
              <div className="p-4 bg-slate-50 rounded-lg border">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-semibold text-lg">{refundOriginalSale.receiptNumber}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(refundOriginalSale.createdAt).toLocaleString('en-PH')}
                    </p>
                    <p className="text-sm text-slate-500">
                      Cashier: {refundOriginalSale.cashierName}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Original Total</p>
                    <p className="font-bold text-lg">{formatCurrency(refundOriginalSale.totalAmount)}</p>
                    {refundOriginalSale.totalRefunded > 0 && (
                      <p className="text-sm text-red-600">
                        Already refunded: {formatCurrency(refundOriginalSale.totalRefunded)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Return Type Selection */}
              <div className="space-y-2">
                <Label>Return Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setRefundType('refund')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      refundType === 'refund' 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <RotateCcw size={24} className="mx-auto mb-2 text-amber-600" />
                    <p className="font-semibold">Full Refund</p>
                    <p className="text-xs text-slate-500">Return items, give money back</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRefundType('exchange')}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      refundType === 'exchange' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <ArrowLeftRight size={24} className="mx-auto mb-2 text-blue-600" />
                    <p className="font-semibold">Exchange</p>
                    <p className="text-xs text-slate-500">Return items for different products</p>
                  </button>
                </div>
              </div>

              {/* Items to Return */}
              <div className="space-y-2">
                <Label>Select Items to Return</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {refundItems.map((item, index) => (
                    <div 
                      key={index} 
                      className={`p-3 rounded-lg border ${
                        item.returnQuantity > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <p className="font-medium">{item.productName}</p>
                          <p className="text-sm text-slate-500">
                            {item.quantity} {item.unit} x {formatCurrency(item.unitPrice)} = {formatCurrency(item.totalPrice)}
                          </p>
                          {item.refundedQuantity > 0 && (
                            <p className="text-xs text-red-500">
                              Already returned: {item.refundedQuantity} {item.unit}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {item.maxReturn > 0 ? (
                            <>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => updateRefundItemQuantity(index, item.returnQuantity - 1)}
                                disabled={item.returnQuantity <= 0}
                              >
                                <Minus size={14} />
                              </Button>
                              <span className="w-12 text-center font-semibold">
                                {item.returnQuantity}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => updateRefundItemQuantity(index, item.returnQuantity + 1)}
                                disabled={item.returnQuantity >= item.maxReturn}
                              >
                                <Plus size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => updateRefundItemQuantity(index, item.maxReturn)}
                                className="text-xs text-amber-600"
                              >
                                All
                              </Button>
                            </>
                          ) : (
                            <span className="text-sm text-red-500">Fully returned</span>
                          )}
                        </div>
                      </div>
                      {item.returnQuantity > 0 && (
                        <div className="mt-2 pt-2 border-t border-amber-200 flex justify-between text-sm">
                          <span className="text-amber-700">Return amount:</span>
                          <span className="font-semibold text-amber-700">
                            {formatCurrency(item.returnQuantity * item.unitPrice)}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label>Reason for Return</Label>
                <Select value={refundReason} onValueChange={setRefundReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wrong_item">Wrong item purchased</SelectItem>
                    <SelectItem value="damaged">Product damaged</SelectItem>
                    <SelectItem value="quality">Quality issue</SelectItem>
                    <SelectItem value="changed_mind">Customer changed mind</SelectItem>
                    <SelectItem value="expired">Expired/Near expiry</SelectItem>
                    <SelectItem value="overcharge">Overcharged</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Refund Summary */}
              {refundItems.some(item => item.returnQuantity > 0) && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-amber-700">
                        {refundItems.filter(i => i.returnQuantity > 0).length} item(s) selected
                      </p>
                      <p className="font-bold text-lg text-amber-800">
                        {refundType === 'refund' ? 'Refund Amount' : 'Exchange Credit'}:
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-amber-700">
                      {formatCurrency(
                        refundItems.reduce((sum, item) => sum + (item.returnQuantity * item.unitPrice), 0)
                      )}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {!refundOriginalSale && !loadingRefund && (
            <div className="py-8 text-center text-slate-400">
              <Receipt size={48} className="mx-auto mb-4 opacity-50" />
              <p>Enter a receipt number to find the sale</p>
              <p className="text-sm">You can find it on the customer's receipt</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {refundOriginalSale && (
            <Button 
              onClick={processRefund}
              disabled={loadingRefund || !refundItems.some(i => i.returnQuantity > 0) || !refundReason}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="process-refund-btn"
            >
              {loadingRefund ? (
                <><Loader2 size={18} className="mr-2 animate-spin" /> Processing...</>
              ) : (
                <><RotateCcw size={18} className="mr-2" /> Process {refundType === 'exchange' ? 'Exchange' : 'Refund'}</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RefundSuccessDialog = ({
  open,
  onOpenChange,
  lastRefund,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 size={24} />
            {lastRefund?.refundType === 'exchange' ? 'Exchange' : 'Refund'} Processed
          </DialogTitle>
        </DialogHeader>
        
        {lastRefund && (
          <div className="space-y-4">
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-600 mb-1">Refund Number</p>
              <p className="font-mono font-bold text-lg">{lastRefund.refundNumber}</p>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Original Receipt:</span>
                <span className="font-medium">{lastRefund.originalReceiptNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Items Returned:</span>
                <span className="font-medium">{lastRefund.items?.length || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Type:</span>
                <span className="font-medium capitalize">{lastRefund.refundType}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                <span>{lastRefund.refundType === 'exchange' ? 'Exchange Credit' : 'Refund Amount'}:</span>
                <span className="text-emerald-600">{formatCurrency(lastRefund.refundAmount)}</span>
              </div>
            </div>
            
            <div className="text-center text-sm text-slate-500">
              <p>Inventory has been restored for returned items.</p>
              {lastRefund.refundType === 'refund' && (
                <p className="mt-1 font-medium text-amber-600">
                  Please give {formatCurrency(lastRefund.refundAmount)} to the customer.
                </p>
              )}
              {lastRefund.refundType === 'exchange' && (
                <p className="mt-1 font-medium text-blue-600">
                  Customer has {formatCurrency(lastRefund.refundAmount)} credit for exchange.
                </p>
              )}
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button 
            onClick={() => onOpenChange(false)}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefundDialog;
